import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputAdornment, MenuItem, TablePagination, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
// import { CiFilter } from "react-icons/ci";
import { StyledWelcomeBanner, StyledWrapper } from "../../dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
  StyledOrdersContainer,
  StyledStatusFilter,
  StyledTableLayout,
  StyledStatusBadge,
} from "./style";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../../virtual-locker/style";
// import { StyledButtonSend } from "../../../components/chat-box/style";
import { CompactStatistics } from "../../../components/admin/dashboard/StatisticsComponent";
import { adminProductIntakeStatisticsData } from "../../dashboard/constant";
import { PRODUCT_LIST } from "../../../actions/admin/customer-actions";
import ProductTable from "../../../components/common/table/ProductTable";
import ProductImageThumbnails from "../../../components/common/table/ProductImageThumbnails";
import ProductOrderIdCell from "../../../components/common/table/ProductOrderIdCell";
import PaymentStatusCell from "../../../components/admin/products/PaymentStatusCell";
import * as _ from "lodash"
import { productOrderType, productStatusColors, PRODUCT_STATUS_FILTER_OPTIONS} from "../../../utils/common";
import DotIcon from '../../../assets/svg/dot-icon.svg';
import Loader from "../../../components/common/loader/Loader";
import {
  formatWeightDisplay,
  toPlainWeightNumber,
} from "../../../components/modals/order-view-items/orderItemDisplay";

const formatProductStatusLabel = (status) => {
  if (status === "shopping_in_progress") return "In Progress";
  if (status === "shopping_completed") return "Shopping Completed";
  if (status === "received_at_the_warehouse") return "Received at the warehouse";
  return _.startCase(status);
};

const productStatusFilterOptions = PRODUCT_STATUS_FILTER_OPTIONS.map((option) =>
  option.value === "shopping_in_progress"
    ? { ...option, label: "In Progress" }
    : option
);

const ProductManagement = () => {
  const dispatch = useDispatch();

  const [statisticsData, setStatisticsData] = useState(adminProductIntakeStatisticsData);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const productListFunc = useCallback(() => {
    dispatch({
      type: PRODUCT_LIST, payload: {
        search: debouncedSearch,
        status: statusFilter,
        page,
        limit: rowsPerPage
      }
    });
  }, [dispatch, debouncedSearch, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    productListFunc();
  }, [productListFunc]);

  const { productsList, productIntakeSummary, total, isLoading } = useSelector(
    (state) => state?.product
  );

  const columns = [
    { key: "rowNo", label: "NO" },
    { key: "order_id", label: "ORDER ID" },
    { key: "order_type", label: "TYPE" },
    { key: "source", label: "SOURCE" },
    { key: "product_name", label: "PRODUCT NAME" },
    { key: "customer_name", label: "CUSTOMER NAME" },
    { key: "shopper_name", label: "SHOPPER NAME" },
    { key: "quantity", label: "QTY" },
    { key: "weight", label: "WEIGHT" },
    { key: "product_images", label: "IMAGE", minWidth: 120 },
    { key: "status", label: "STATUS" },
    { key: "payment_status", label: "PAYMENT" },
    { key: "isShopperAssigned", label: "ASSIGNED" },
    { key: "actions", label: "ACTIONS" },
  ];

  const allRecords =
    productsList?.map((item, index) => {
      return {
        rowNo: (page - 1) * rowsPerPage + index + 1,
        order_id: (
          <ProductOrderIdCell
            orderId={item.order_id}
            orderNumber={item.order_number}
          />
        ),
        order_type: productOrderType[item.order_type],
        raw_order_type: item.order_type,
        source: item.source_name || "-",
        product_name: (
          <Link
            component={RouterLink}
            to={`/admin/items/${item.id}`}
            underline="hover"
            color="primary"
            sx={{ fontWeight: "normal", cursor: "pointer" }}
          >
            {item.product_name}
          </Link>
        ),
        id: item.id,
        raw_quantity: item.quantity,
        raw_weight: toPlainWeightNumber(item.weight),
        price: item.approx_rate,
        note_to_customer: item.note_to_customer || "",
        shopper_name: item.personal_shopper?.name || "-",
        quantity: item.quantity ?? "-",
        product_image: item.product_image || [],
        invoice_of_product: item.invoice_of_product || null,
        product_images: (
          <ProductImageThumbnails
            images={item.product_image || []}
            productName={item.product_name}
            productId={item.id}
            recordList={productListFunc}
          />
        ),
        activeStatus: item.status,
        activePaymentStatus: item.payment_status || "",
        payment_status: item.order_type === "personal_shopper" ? (
          <PaymentStatusCell row={{ ...item, activePaymentStatus: item.payment_status }} recordList={productListFunc} />
        ) : "-",
        isShopperAssigned: (item.order_type === "personal_shopper" && item.status === "approved") ? (
          <StyledStatusBadge background={productStatusColors.completed?.background}>
            <img src={DotIcon} alt="" /> Completed
          </StyledStatusBadge>
        ) : item?.isShopperAssigned ? (
          <StyledStatusBadge background={productStatusColors.pending?.background}>
            <img src={DotIcon} alt="" /> Assigned
          </StyledStatusBadge>
        ) : "",
        weight: formatWeightDisplay(item.weight),
        status: (
          <StyledStatusBadge background={productStatusColors[item.status]?.background}>
            <img src={DotIcon} alt="" /> {formatProductStatusLabel(item.status)}
          </StyledStatusBadge>
        ),

      }
    }) || [];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler); // cleanup on each change
    };
  }, [searchTerm]);

  useEffect(() => {
    setStatisticsData(
      adminProductIntakeStatisticsData.map((item) => ({
        ...item,
        value: productIntakeSummary?.total_pending_items ?? 0,
      }))
    );
  }, [productIntakeSummary]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  return (
    <StyledWrapper>
      <StyledWelcomeBanner>
        <CompactStatistics idDataList={statisticsData || []} />
      </StyledWelcomeBanner>

      <StyledTableLayout>
          <StyledTypographyText>PRODUCT INTAKE</StyledTypographyText>

          <StyledBoxStyle>
            <StyledCustomerSearch
              placeholder="Search Order ID, Product Name, Customer Name"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StyledSearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StyledStatusFilter
              value={statusFilter}
              onChange={handleStatusFilterChange}
              displayEmpty
              inputProps={{ "aria-label": "Filter by status" }}
            >
              {productStatusFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledStatusFilter>
          </StyledBoxStyle>

          <StyledOrdersContainer>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <ProductTable
                  columns={columns}
                  rows={allRecords}
                  recordList={productListFunc}
                />
                <TablePagination
                  component="div"
                  count={total || 0}
                  page={page - 1}
                  onPageChange={(event, newPage) => setPage(newPage + 1)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </StyledOrdersContainer>
        </StyledTableLayout>
    </StyledWrapper>
  );
};

export default ProductManagement;
